// WlcAddressField
// ----------------
// Multi-line address input for the WLC screener.
// Matches the address structure from form_data_model.py:
//   - Street address (required)
//   - City / Town (required)
//   - Postcode (required, basic UK postcode format validation on blur)
// Emits a structured object { streetAddress, city, postcode } via onChange.

import React, { useState, useEffect, useCallback } from 'react';

const BRAND_ACCENT = '#e7c22f';

// Loose UK postcode regex — covers standard formats like "SW1A 1AA", "M1 1AA", "B33 8TH"
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export default function WlcAddressField({ value, onChange }) {
    const [streetAddress, setStreetAddress] = useState('');
    const [city, setCity] = useState('');
    const [postcode, setPostcode] = useState('');
    const [postcodeHint, setPostcodeHint] = useState('');

    // Sync from external value on mount / value change
    useEffect(() => {
        if (value && typeof value === 'object') {
            setStreetAddress(value.streetAddress || '');
            setCity(value.city || '');
            setPostcode(value.postcode || '');
        }
    }, [value]);

    const emit = useCallback((sa, c, pc) => {
        onChange?.({ streetAddress: sa, city: c, postcode: pc });
    }, [onChange]);

    const handlePostcodeBlur = () => {
        if (postcode && !UK_POSTCODE_REGEX.test(postcode.trim())) {
            setPostcodeHint('Please enter a valid UK postcode.');
        } else {
            setPostcodeHint('');
        }
    };

    const inputClass = "w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200";
    const inputStyle = { '--tw-ring-color': BRAND_ACCENT, '--tw-ring-opacity': '0.5' };

    return (
        <div className="space-y-3">
            {/* Street address */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Street address
                </label>
                <input
                    type="text"
                    value={streetAddress}
                    onChange={(e) => { setStreetAddress(e.target.value); emit(e.target.value, city, postcode); }}
                    className={inputClass}
                    style={inputStyle}
                />
            </div>

            {/* City / Town */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    City or town
                </label>
                <input
                    type="text"
                    value={city}
                    onChange={(e) => { setCity(e.target.value); emit(streetAddress, e.target.value, postcode); }}
                    className={inputClass}
                    style={inputStyle}
                />
            </div>

            {/* Postcode */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Postcode
                </label>
                <input
                    type="text"
                    value={postcode}
                    onChange={(e) => { setPostcode(e.target.value); emit(streetAddress, city, e.target.value); }}
                    onBlur={handlePostcodeBlur}
                    className={`${inputClass} uppercase`}
                    style={inputStyle}
                />
                {postcodeHint && (
                    <p className="text-red-500 text-xs mt-1">{postcodeHint}</p>
                )}
            </div>
        </div>
    );
}
